(() => {
  'use strict';
  if (window.__GREMARI_COMPARE_V1__) return;
  window.__GREMARI_COMPARE_V1__ = true;

  const STORAGE_KEY = 'gremari_compare_v1';
  const MAX_ITEMS = 5;
  const cfg = () => window.GremariCommerceTools || {};
  const labels = () => cfg().labels || {};
  const routes = () => cfg().routes || {};

  const CARD_SELECTOR = [
    'product-card',
    '.card-product',
    '.product-card',
    '.product-card-wrapper',
    '.card-wrapper',
    '.card--product',
    '.product-grid-item',
    '.grid__item',
    '.gremari-recommendation-card',
    '[data-product-card]'
  ].join(',');

  const MEDIA_SELECTOR = [
    '.card-media',
    '.card__media',
    '.card__inner',
    '.product-card__media',
    '.product-media-container',
    '.media',
    '[data-product-card-media]'
  ].join(',');

  const ICON = `
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M7 7h11"></path>
      <path d="m15 4 3 3-3 3"></path>
      <path d="M17 17H6"></path>
      <path d="m9 14-3 3 3 3"></path>
    </svg>`;

  let timer = 0;
  let toastTimer = 0;

  function handleFromUrl(url) {
    try {
      const parsed = new URL(url, location.origin);
      const match = parsed.pathname.match(/\/products\/([^/?#]+)/);
      return match ? decodeURIComponent(match[1]) : '';
    } catch (_) {
      const match = String(url || '').match(/\/products\/([^/?#]+)/);
      return match ? match[1] : '';
    }
  }

  function normalize(item) {
    if (typeof item === 'string') {
      const handle = item.trim();
      return handle ? { handle, url: `/products/${handle}` } : null;
    }
    if (!item || typeof item !== 'object') return null;
    let handle = String(item.handle || '').trim();
    if (!handle && item.url) handle = handleFromUrl(item.url);
    if (!handle) return null;
    return {
      handle,
      title: String(item.title || item.data?.title || ''),
      url: item.url || item.data?.url || `/products/${handle}`,
      image: item.image || item.data?.image || '',
      data: item.data && typeof item.data === 'object' ? item.data : null
    };
  }

  function unique(items) {
    const seen = new Set();
    return items.filter((item) => {
      if (!item?.handle || seen.has(item.handle)) return false;
      seen.add(item.handle);
      return true;
    });
  }

  function read() {
    try {
      const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      return unique((Array.isArray(parsed) ? parsed : []).map(normalize).filter(Boolean)).slice(0, MAX_ITEMS);
    } catch (_) {
      return [];
    }
  }

  function write(items) {
    const safe = unique(items.map(normalize).filter(Boolean)).slice(0, MAX_ITEMS);
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(safe)); } catch (_) {}
    sync(safe);
    window.dispatchEvent(new CustomEvent('gremari:compare:changed', { detail: { items: safe } }));
    return safe;
  }

  function showToast(message) {
    let node = document.querySelector('.gmc-toast');
    if (!node) {
      node = document.createElement('div');
      node.className = 'gmc-toast';
      node.setAttribute('role', 'status');
      document.body.appendChild(node);
    }
    node.textContent = message;
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => node.remove(), 2200);
  }

  function productLink(card) {
    if (!card) return null;
    const links = card.matches?.('a[href*="/products/"]')
      ? [card]
      : [...(card.querySelectorAll?.('a[href*="/products/"]') || [])];
    return links.find((a) => handleFromUrl(a.getAttribute('href'))) || null;
  }

  function fallback(button) {
    const card = button.closest(CARD_SELECTOR);
    const link = productLink(card);
    const handle = String(button.dataset.handle || handleFromUrl(link?.href || '')).trim();
    const title = card?.querySelector('.card-heading,.card-product-title,.product-card__title,h2,h3,[data-product-title]');
    const image = card?.querySelector('img');
    return {
      handle,
      title: (title?.textContent || '').trim(),
      url: link?.href || `/products/${handle}`,
      image: image?.currentSrc || image?.src || ''
    };
  }

  async function fetchProductData(handle) {
    const root = window.Shopify?.routes?.root || '/';
    const response = await fetch(
      `${root}products/${encodeURIComponent(handle)}?section_id=gremari-compare-product-data`,
      {
        credentials: 'same-origin',
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      }
    );
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const script = doc.querySelector('[data-gm-compare-product-json]');
    if (!script) throw new Error('Missing compare data');
    const data = JSON.parse(script.textContent || '{}');
    if (!data?.handle) throw new Error('Incomplete compare data');
    return data;
  }

  function ensureFab(items = read()) {
    let fab = document.querySelector('[data-gmc-fab]');
    if (!fab) {
      fab = document.createElement('div');
      fab.className = 'gmc-fab';
      fab.dataset.gmcFab = '1';
      fab.innerHTML = `
        <a class="gmc-fab__button" href="${routes().compare || '/pages/compare'}"
           aria-label="${labels().compareTitle || 'Compare'}"
           title="${labels().compareTitle || 'Compare'}">
          ${ICON}
          <span class="gmc-fab__count" data-gmc-fab-count hidden>0</span>
        </a>`;
      document.body.appendChild(fab);
    }
    const count = fab.querySelector('[data-gmc-fab-count]');
    if (count) {
      count.textContent = String(items.length);
      count.hidden = items.length === 0;
    }
  }

  function sync(items = read()) {
    const handles = new Set(items.map((x) => x.handle));
    document.querySelectorAll('[data-gmc-action="toggle"][data-handle]').forEach((button) => {
      const active = handles.has(button.dataset.handle);
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
      const label = active
        ? (labels().compareRemove || 'Remove from compare')
        : (labels().compareAdd || 'Add to compare');
      button.setAttribute('aria-label', label);
      button.title = label;
    });
    ensureFab(items);
  }

  function decorateCard(card) {
    if (!(card instanceof Element) || card.dataset.gmcReady === '1') return;
    const link = productLink(card);
    const handle = handleFromUrl(link?.getAttribute('href') || '');
    if (!handle) return;
    const media = card.querySelector(MEDIA_SELECTOR);
    if (!media) return;

    card.dataset.gmcReady = '1';
    media.classList.add('gmc-card-media');
    if (media.querySelector(':scope > .gmc-card-btn')) return;

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'gmc-card-btn';
    button.dataset.gmcAction = 'toggle';
    button.dataset.handle = handle;
    button.innerHTML = ICON;
    media.appendChild(button);
  }

  function decorate(scope = document) {
    if (scope instanceof Element && scope.matches(CARD_SELECTOR)) decorateCard(scope);
    scope.querySelectorAll?.(CARD_SELECTOR).forEach(decorateCard);
    sync();
  }

  async function toggle(handle, button) {
    let items = read();
    const found = items.findIndex((x) => x.handle === handle);

    if (found >= 0) {
      items.splice(found, 1);
      write(items);
      return;
    }

    if (items.length >= MAX_ITEMS) {
      showToast(labels().compareMax || `You can compare up to ${MAX_ITEMS} products.`);
      return;
    }

    button.disabled = true;
    button.classList.add('is-loading');

    let data = null;
    try { data = await fetchProductData(handle); } catch (_) {}

    const fb = fallback(button);
    const item = normalize({
      handle,
      title: data?.title || fb.title,
      url: data?.url || fb.url,
      image: data?.image || fb.image,
      data
    });

    if (item) {
      items.push(item);
      write(items);
      showToast(labels().compareAdded || 'Added to compare.');
    }

    button.disabled = false;
    button.classList.remove('is-loading');
  }

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-gmc-action="toggle"][data-handle]');
    if (!button) return;
    event.preventDefault();
    event.stopPropagation();
    const handle = String(button.dataset.handle || '').trim();
    if (handle) toggle(handle, button);
  }, true);

  function boot() {
    decorate(document);
    ensureFab();

    const observer = new MutationObserver((mutations) => {
      const relevant = mutations.some((mutation) =>
        [...mutation.addedNodes].some((node) =>
          node instanceof Element &&
          (node.matches?.(CARD_SELECTOR) || node.querySelector?.(CARD_SELECTOR))
        )
      );
      if (!relevant) return;
      clearTimeout(timer);
      timer = setTimeout(() => decorate(document), 80);
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  window.GremariCompare = {
    getItems: read,
    setItems: write,
    remove(handle) { write(read().filter((x) => x.handle !== handle)); },
    clear() { write([]); },
    move(handle, direction) {
      const items = read();
      const index = items.findIndex((x) => x.handle === handle);
      const next = index + Number(direction || 0);
      if (index < 0 || next < 0 || next >= items.length) return items;
      [items[index], items[next]] = [items[next], items[index]];
      return write(items);
    },
    fetchProductData,
    refreshCart() {
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      document.dispatchEvent(new CustomEvent('cart:update', { bubbles: true }));
    }
  };
})();
